
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import HeaderLogo from '@/components/header/HeaderLogo';
import HeaderNavLinks, { ReaderNavLinks } from '@/components/header/HeaderNavLinks';
import HeaderSearch from '@/components/header/HeaderSearch';
import NotificationBell from '@/components/header/NotificationBell';
import HeaderProfile from '@/components/header/HeaderProfile';
import MobileMenu from '@/components/header/MobileMenu';
import NewArticleButton from '@/components/header/NewArticleButton';
import ModeSwitcher from '@/components/header/ModeSwitcher';
import { useAuth } from '@/hooks/useAuth';
import { useHeaderState } from '@/components/header/HeaderUtils';
import { toast } from '@/components/ui/use-toast';

interface HeaderWithAuthProps {
  children: React.ReactNode;
}

const HeaderWithAuth: React.FC<HeaderWithAuthProps> = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, userProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract user info
  const displayName = userProfile?.display_name || user?.user_metadata?.display_name || user?.email?.split('@')[0] || 'User';
  const profileImage = userProfile?.avatar_url || null;
  const userRole = userProfile?.user_role || user?.user_metadata?.user_role || 'reader';
  
  // Get current mode info
  const { isJournalist, isInReaderMode, isInCreatorMode } = useHeaderState(userRole);
  
  return (
    <div className="flex flex-col min-h-screen w-full bg-background text-foreground">
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <HeaderLogo navigateTo={isInReaderMode || !isJournalist ? '/home' : '/dashboard'} />
            <HeaderNavLinks />
            <ModeSwitcher 
              isJournalist={isJournalist} 
              isInReaderMode={isInReaderMode} 
              isInCreatorMode={isInCreatorMode} 
            />
          </div>
          
          <div className="flex items-center gap-2 md:gap-4">
            <HeaderSearch />
            <NotificationBell count={0} />
            <NewArticleButton isJournalist={isJournalist} />
            <HeaderProfile 
              displayName={displayName}
              profileImage={profileImage}
              notificationCount={0}
              subscriptionStatus="Free"
              walletBalance={0}
              draftCount={0}
              isJournalist={isJournalist}
              isInReaderMode={isInReaderMode}
              isInCreatorMode={isInCreatorMode}
            />
            <button 
              className="md:hidden text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            </button>
            <MobileMenu 
              isOpen={mobileMenuOpen} 
              isInReaderMode={isInReaderMode} 
              isJournalist={isJournalist}
              onClose={() => setMobileMenuOpen(false)}
            >
              <ReaderNavLinks />
            </MobileMenu>
          </div>
        </div>
      </header>
      
      <main className="flex-grow">
        {children}
      </main>
    </div>
  );
};

export default HeaderWithAuth;
